class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', event => {
      event.preventDefault();
      const cartItems =
        this.closest('cart-drawer-items') ||
        this.closest('cart-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}
customElements.define('cart-remove-button', CartRemoveButton);

class ProgressBar extends HTMLElement {
  constructor() {
    super();
    this.progress = this.getAttribute('data-progress');
  }

  connectedCallback() {
    setTimeout(() => {
      this.querySelector('[data-progress-line]').style.width = this.progress;
    }, 100);
  }
}
customElements.define('progress-bar', ProgressBar);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.currentItemCount = Array.from(
      this.querySelectorAll('[name="updates[]"]')
    ).reduce(
      (total, quantityInput) => total + parseInt(quantityInput.value),
      0
    );
    this.debouncedOnChange = debounce(event => {
      this.onChange(event);
    }, 300);
    this.addEventListener(
      'change',
      this.debouncedOnChange.bind(this)
    );
    this.addEventListener('click', event => {
      const edit = event.target.closest('[data-cart-edit]');
      if (!edit || !this.contains(edit)) return;
      event.preventDefault();
      event.stopPropagation();
      this.openEditor(edit.closest('.cart-item'));
    });

    updateFreeShipping();
  }

  onChange(event) {
    if (event.target.name !== 'updates[]') return;

    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name')
    );
  }

  getSectionsToRender() {
    return [
      {
        id: `#shopify-section-${
          document.getElementById('main-cart-items').dataset.id
        }`,
        section:
          document.getElementById('main-cart-items').dataset.id,
        selector: `#shopify-section-${
          document.getElementById('main-cart-items').dataset.id
        } cart-items`
      },
      {
        id: '#cart-counter',
        section: 'cart-counter',
        selector: '#shopify-section-cart-counter'
      }
    ];
  }

  editorElement() {
    return document.querySelector('[data-cart-editor]');
  }

  openEditor(item) {
    const source = item?.querySelector('[data-cart-edit-source]');
    const editor = this.editorElement();
    if (!source || !editor) return;
    const data = JSON.parse(source.textContent);
    editor._cartItems = this;
    editor._item = item;
    editor._data = data;
    editor._color = data.color || '';
    editor._size = data.size || '';
    if (!editor.dataset.bound) {
      editor.dataset.bound = 'true';
      editor.addEventListener('click', event => {
        event.stopPropagation();
        const owner = editor._cartItems;
        if (!owner) return;
        if (event.target.closest('[data-editor-close]')) {
          owner.closeEditor();
          return;
        }
        const color = event.target.closest('[data-editor-color]');
        if (color && !color.disabled) {
          editor._color = color.dataset.editorColor;
          owner.renderEditor();
          return;
        }
        const size = event.target.closest('[data-editor-size]');
        if (size && !size.disabled) {
          editor._size = size.dataset.editorSize;
          owner.renderEditor();
          return;
        }
        if (event.target.closest('[data-editor-confirm]')) owner.confirmEditor();
      });
    }
    this.renderEditor();
    liftCartEditor();
    const drawer = document.querySelector('cart-drawer');
    if (drawer && !drawer.classList.contains('is-visible')) drawer.open();
    const inner = editor.closest('.cart-drawer__inner');
    if (inner) inner.scrollTop = 0;
    if (inner && !inner.dataset.editorBound) {
      inner.dataset.editorBound = 'true';
      inner.addEventListener('click', event => {
        if (!inner.classList.contains('is-editing')) return;
        const path = event.composedPath();
        if (path.some(node => node.classList?.contains('cart-item-editor'))) return;
        editor._cartItems?.closeEditor();
      });
    }
    editor.hidden = false;
    inner?.classList.remove('is-closing');
    editor._slide = (editor._slide || 0) + 1;
    void editor.offsetHeight;
    inner?.classList.add('is-editing');
  }

  closeEditor() {
    const editor = this.editorElement();
    if (!editor || editor.hidden) return;
    const inner = editor.closest('.cart-drawer__inner');
    inner?.classList.remove('is-editing');
    const token = (editor._slide = (editor._slide || 0) + 1);
    const done = event => {
      if (event?.propertyName && event.propertyName !== 'transform') return;
      if (editor._slide !== token) return;
      editor.hidden = true;
    };
    editor.addEventListener('transitionend', done);
    window.setTimeout(() => {
      editor.removeEventListener('transitionend', done);
      done();
    }, 500);
  }

  findEditorVariant(editor, color = editor._color, size = editor._size) {
    const data = editor._data;
    const wanted = {
      option1: editor._item?.dataset.option1 || '',
      option2: editor._item?.dataset.option2 || '',
      option3: editor._item?.dataset.option3 || ''
    };
    if (data.colorPosition) wanted[`option${data.colorPosition}`] = color;
    if (data.sizePosition) wanted[`option${data.sizePosition}`] = size;
    return (data.variants || []).find(entry =>
      (entry.option1 || '') === wanted.option1 &&
      (entry.option2 || '') === wanted.option2 &&
      (entry.option3 || '') === wanted.option3
    );
  }

  renderEditor() {
    const editor = this.editorElement();
    const data = editor?._data;
    if (!data) return;
    const image = editor.querySelector('[data-editor-image]');
    image.src = data.image || '';
    image.alt = data.title || '';
    image.hidden = !data.image;
    editor.querySelector('[data-editor-name]').textContent = data.title || '';
    editor.querySelector('[data-editor-price]').textContent = data.price || '';

    const colors = data.colors || [];
    editor.querySelector('[data-editor-color-group]').hidden = colors.length === 0;
    editor.querySelector('[data-editor-color-name]').textContent = editor._color;
    const colorRow = editor.querySelector('[data-editor-colors]');
    colorRow.replaceChildren(...colors.map(color => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `swatch swatch--round swatch--${color.handle} product__swatch--color cart-item-editor__swatch`;
      if (color.name === editor._color) button.classList.add('is-active');
      button.dataset.editorColor = color.name;
      button.setAttribute('aria-label', color.name);
      const variant = this.findEditorVariant(editor, color.name, editor._size);
      button.disabled = !variant?.available;
      return button;
    }));

    const sizes = data.sizes || [];
    editor.querySelector('[data-editor-size-group]').hidden = sizes.length === 0;
    editor.querySelector('[data-editor-size-name]').textContent = editor._size;
    const sizeRow = editor.querySelector('[data-editor-sizes]');
    sizeRow.replaceChildren(...sizes.map(value => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'cart-item-editor__size';
      if (value === editor._size) button.classList.add('is-active');
      button.dataset.editorSize = value;
      button.textContent = value;
      const variant = this.findEditorVariant(editor, editor._color, value);
      button.disabled = !variant?.available;
      return button;
    }));

    const selected = this.findEditorVariant(editor);
    if (selected?.image) {
      image.src = selected.image;
      image.hidden = false;
    }
    if (selected?.price) {
      editor.querySelector('[data-editor-price]').textContent = selected.price;
    }
    editor.querySelector('[data-editor-confirm]').disabled = !selected?.available;
  }

  confirmEditor() {
    const editor = this.editorElement();
    const variant = editor && this.findEditorVariant(editor);
    const item = editor?._item;
    if (!variant?.available || !item) return;
    const same =
      (item.dataset.option1 || '') === (variant.option1 || '') &&
      (item.dataset.option2 || '') === (variant.option2 || '') &&
      (item.dataset.option3 || '') === (variant.option3 || '');
    if (same) {
      this.closeEditor();
      return;
    }
    this.changeVariant(item, variant.id);
  }

  changeVariant(item, variantId) {
    if (!item || !variantId) return;

    const quantity = parseInt(item.dataset.quantity, 10) || 1;
    let properties = {};
    try {
      properties = JSON.parse(item.dataset.properties || '{}');
    } catch (error) {
      properties = {};
    }
    if (Array.isArray(properties)) {
      properties = Object.fromEntries(properties);
    }

    this.classList.add('is-loading');
    const sections = this.getSectionsToRender().map(section => section.section);

    fetch(`${routes.cart_change_url}`, {
      ...fetchConfig(),
      body: JSON.stringify({ id: item.dataset.key, quantity: 0 })
    })
      .then(response => response.json())
      .then(state => {
        if (state.status || state.errors) {
          throw new Error(state.description || 'cart change failed');
        }
        const line = { id: Number(variantId), quantity };
        if (properties && Object.keys(properties).length) line.properties = properties;
        return fetch(`${routes.cart_add_url}`, {
          ...fetchConfig(),
          body: JSON.stringify({
            items: [line],
            sections,
            sections_url: window.location.pathname
          })
        });
      })
      .then(response => response.json())
      .then(response => {
        if (response.status || !response.sections) return;
        this.closeEditor();
        this.getSectionsToRender()?.forEach(section => {
          const elementToReplace = document.querySelector(section.selector) || document.querySelector(section.id);
          if (!elementToReplace) return;
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            response.sections[section.section],
            section.selector
          );
        });
        updateFreeShipping();
      })
      .catch(error => {
        console.error(error);
      })
      .finally(() => this.classList.remove('is-loading'));
  }

  updateQuantity(line, quantity, name) {
    this.classList.add('is-loading');

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map(
        section => section.section
      ),
      sections_url: window.location.pathname
    });

    fetch(`${routes.cart_change_url}`, {
      ...fetchConfig(),
      ...{ body }
    })
      .then(response => response.text())
      .then(state => {
        const parsedState = JSON.parse(state);

        this.getSectionsToRender()?.forEach(section => {
          const elementToReplace = document.querySelector(section.selector) || document.querySelector(section.id);


          if (elementToReplace) {
            if (!parsedState.errors) {
              elementToReplace.innerHTML = this.getSectionInnerHTML(
                parsedState.sections[section.section],
                section.selector
              );
            }
          } else {
            console.error(`Element with selector ${section.selector} not found`);
          }
        });
        this.updateLiveRegions(line, parsedState.item_count);
        const lineItem = document.getElementById(`CartItem-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`))
          lineItem.querySelector(`[name="${name}"]`).focus();

        updateFreeShipping();
      })
      .finally(() => this.classList.remove('is-loading'));
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  updateLiveRegions(line, itemCount) {
    if (this.currentItemCount === itemCount) {
      document
        .querySelectorAll(
          `[data-line-item-error][data-line="${line}"]`
        )
        .forEach(error => {
          error.innerHTML = window.cartStrings.quantityError.replace(
            '[quantity]',
            document.querySelector(`[id*="ProductQuantity-${line}"]`)
              .value
          );
        });
    }

    this.currentItemCount = itemCount;
  }
}

customElements.define('cart-items', CartItems);

function liftCartEditor() {
  const bar = document.getElementById('PBarNextFrameWrapper');
  const preview = location.search.includes('preview_theme_id') || window.Shopify?.designMode;
  const offset = bar
    ? Math.ceil(bar.getBoundingClientRect().height) + 8
    : preview
      ? 80
      : 0;
  document.documentElement.style.setProperty('--cart-preview-offset', `${offset}px`);
}

liftCartEditor();
